// src/hooks/useExertionTrendData.js
import { useEffect, useMemo, useState } from "react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { firebaseApp } from "../firebase/firebaseApp";
import { computeRecoveryMetricsChosen } from "../utils/exertion/recovery";

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function formatLabel(ts) {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Your feed item may be:
 * - a "light" activity item (with baseName)
 * - or a "measurement doc-like" object (with measurementId/path but no baseName)
 *
 * Firestore doc id is the measurement id under:
 * measurements/{uid}/{tag}/{docId}
 */
function inferDocId(it) {
  // best case
  if (typeof it?.baseName === "string" && it.baseName.length)
    return it.baseName;

  // common in your measurement object
  if (typeof it?.measurementId === "string" && it.measurementId.length)
    return it.measurementId;

  // parse from storage-ish path:
  // "video_metadata/breatheultra/exertionTest//<uid>/<measurementId>/recovery.json"
  const p = typeof it?.path === "string" ? it.path : "";
  if (p) {
    const parts = p.split("/").filter(Boolean);
    // find ".../<uid>/<measurementId>/recovery.json"
    // measurementId is usually the segment right before the filename
    if (parts.length >= 2) {
      const maybeId = parts[parts.length - 2];
      if (maybeId && maybeId.includes("T")) return maybeId; // defensive heuristic
      if (maybeId) return maybeId;
    }
  }

  return null;
}

async function fetchMeasurementDocs({ uid, tag, ids }) {
  const db = getFirestore(firebaseApp);

  const reads = ids.map(async (id) => {
    const ref = doc(db, "measurements", uid, tag, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { baseName: id, ...snap.data() };
  });

  const out = await Promise.all(reads);
  return out.filter(Boolean);
}

function buildSeriesFromDocs(docs, picker) {
  const rows = [];
  for (const d of docs) {
    const ts = safeNum(d?.ts);
    const v = picker(d);
    if (!ts || v == null) continue;
    rows.push({ ts, v });
  }
  rows.sort((a, b) => a.ts - b.ts);
  return {
    labels: rows.map((r) => formatLabel(r.ts)),
    values: rows.map((r) => r.v),
  };
}

export function useExertionTrendData({
  uid,
  items,
  tag = "breatheultra",
  repo = "pdx-recording-exertion-recovery",
  maxDocs = 30,
}) {
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState(null);

  const ids = useMemo(() => {
    const list = (items || [])
      .filter(Boolean)
      // accept either top-level variant or nested variant
      .filter((it) => (it?.variant || it?.data?.variant) === "exertion")
      // if you rely on repo, accept top-level or nested
      .filter((it) => {
        if (!repo) return true;
        return it?.repo === repo || it?.data?.repo === repo;
      })
      .map((it) => ({
        id: inferDocId(it),
        ts: Number(it?.ts ?? it?.timestamp ?? it?.data?.ts ?? 0),
      }))
      .filter((x) => !!x.id)
      .sort((a, b) => (b.ts || 0) - (a.ts || 0))
      .slice(0, maxDocs)
      .map((x) => x.id);

    return Array.from(new Set(list));
  }, [items, repo, maxDocs]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!uid || !ids.length) {
        setDocs([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const fetched = await fetchMeasurementDocs({ uid, tag, ids });
        if (cancelled) return;

        fetched.sort((a, b) => Number(a.ts || 0) - Number(b.ts || 0));
        setDocs(fetched);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [uid, tag, ids]);

  const rrSeries = useMemo(
    () =>
      buildSeriesFromDocs(docs, (d) => {
        const rr = safeNum(d?.estimated_rr_bpm);
        return rr == null ? null : Math.round(rr);
      }),
    [docs]
  );

  const br1Series = useMemo(
    () =>
      buildSeriesFromDocs(docs, (d) => {
        const rec = computeRecoveryMetricsChosen(d);
        const drop = rec?.ok ? safeNum(rec?.rrRecovery?.w60?.drop) : null;
        return drop == null ? null : Math.round(drop * 100) / 100;
      }),
    [docs]
  );

  const br2Series = useMemo(
    () =>
      buildSeriesFromDocs(docs, (d) => {
        const rec = computeRecoveryMetricsChosen(d);
        const drop = rec?.ok ? safeNum(rec?.rrRecovery?.w120?.drop) : null;
        return drop == null ? null : Math.round(drop * 100) / 100;
      }),
    [docs]
  );

  // Keep as seconds (numeric); format on axis later if needed
  const rt90Series = useMemo(
    () =>
      buildSeriesFromDocs(docs, (d) => {
        const rec = computeRecoveryMetricsChosen(d);
        const s = rec?.ok ? safeNum(rec?.timeToBaseline?.seconds90) : null;
        return s == null ? null : Math.round(s);
      }),
    [docs]
  );

  const rt95Series = useMemo(
    () =>
      buildSeriesFromDocs(docs, (d) => {
        const rec = computeRecoveryMetricsChosen(d);
        const s = rec?.ok ? safeNum(rec?.timeToBaseline?.seconds95) : null;
        return s == null ? null : Math.round(s);
      }),
    [docs]
  );

  return {
    loading,
    error,
    docs,
    rrSeries,
    br1Series,
    br2Series,
    rt90Series,
    rt95Series,
  };
}
